import { googleEvent } from "../utils/dom";
import shared from "../data/js/shared";
import { useState, useEffect, useCallback } from "react";
import { useSession } from "next-auth/react";
import { useNotifications, useUI, useUser, useRouterContext } from "../contexts";
import PanelDismiss from "./panel-dismiss";
import CTABox from "./cta-box";
import type { DBNotification, Notification as NotificationType } from "../contexts/NotificationProvider";
import { PAGE_SIZE } from "../data/js/pagination";
import { NOTIFICATION_AUTOCLOSE_TIMEOUT } from "../data/js/server-constants";
import { useToastNotifications } from "../hooks/useToastNotifications";
import { trackError } from "../utils/error-tracking";
import { trackFunnelEvent } from "../utils/track-funnel";
import { MISSING_ADDRESS_CATEGORY } from "../utils/missing-address-notice-shared";
import { asError } from '../hooks/as-error';

const {
  header,
  noNotificationsCTAHeading,
  noNotificationsCTAExplain,
  noNotificationsCTAButton,
} = shared.notifications;

const Notification = ({
  msg,
  classType,
  dismiss,
  index,
  push,
  actionURL,
  setNotificationDrawerOpen,
}) => {
  if (!["success", "error", "action"].includes(classType)) return "";
  return (
    <div
      className={`notification same-color-3 ${classType}`}
      onClick={() => {
        dismiss(index);
        if (classType === "action") {
          googleEvent("action_notification", { actionURL });
          push(actionURL);
          setNotificationDrawerOpen(false);
        }
      }}
    >
      {msg}
    </div>
  );
};

// Database notification component
const DBNotificationItem = ({ notification, push, setNotificationDrawerOpen, markRead, deleteNotif }: {
  notification: DBNotification;
  push: (url: string) => void;
  setNotificationDrawerOpen: (open: boolean) => void;
  markRead: (id: string) => Promise<void>;
  deleteNotif: (id: string) => Promise<void>;
}) => {
  const handleClick = async () => {
    // Mark as read (notification removed optimistically)
    if (!notification.isRead) {
      void markRead(notification.id);
    }

    // The middle step of the `seller_address` funnel: the seller was asked for
    // a shipping address and cared enough to act. Fired before the navigation
    // rather than on the destination page, because /account/addresses is
    // reachable from the menu too and counting arrivals there would credit the
    // notice with sellers who never saw it.
    if (notification.metadata?.category === MISSING_ADDRESS_CATEGORY) {
      trackFunnelEvent(
        'seller_address',
        'notice_clicked',
        { notification_id: notification.id },
        { component: 'notification-drawer', immediate: true }
      );
    }

    // Navigate if there's an action URL and close the drawer
    if (notification.actionUrl) {
      push(notification.actionUrl);
      setNotificationDrawerOpen(false);
    }
  };

  const handleDelete = async (e: React.MouseEvent) => {
    e.stopPropagation();
    await deleteNotif(notification.id);
  };

  return (
    <div
      className={`notification db-notification ${notification.isRead ? 'read' : 'unread'} action ${notification.actionUrl ? 'cursor-pointer' : 'cursor-default'}`}
      onClick={handleClick}
    >
      <div className="notification-content">
        <div className="notification-title">{notification.title}</div>
        <div className="notification-message">{notification.message}</div>
        <div className="notification-meta">
          <span className="notification-type">{notification.type}</span>
          <span className="notification-date">
            {new Date(notification.createdDate).toLocaleDateString()}
          </span>
        </div>
      </div>
      <button
        className="notification-delete"
        onClick={handleDelete}
        aria-label="Delete notification"
      >
        ×
      </button>
    </div>
  );
};

// Toast notification component - shows at top-right of screen
const ToastNotification = ({
  notification,
  onDismiss,
}: {
  notification: NotificationType;
  onDismiss: () => void;
}) => {
  const [isExiting, setIsExiting] = useState(false);

  const handleDismiss = useCallback(() => {
    setIsExiting(true);
    setTimeout(onDismiss, 300); // Wait for exit animation
  }, [onDismiss]);

  useEffect(() => {
    const timer = setTimeout(() => {
      handleDismiss();
    }, NOTIFICATION_AUTOCLOSE_TIMEOUT);
    return () => clearTimeout(timer);
  }, [handleDismiss]);

  return (
    <div
      className={`toast-notification toast-${notification.type} ${isExiting ? 'toast-exit' : 'toast-enter'}`}
      onClick={handleDismiss}
    >
      {notification.msg}
    </div>
  );
};

// Toast container - renders toasts at fixed position
export const ToastNotifications = () => {
  const { notifications, dismissNotification } = useNotifications();
  const { visibleToasts, handleDismissToast } = useToastNotifications({
    notifications,
    dismissNotification,
  });

  if (visibleToasts.length === 0) return null;

  return (
    <div className="toast-container">
      {visibleToasts.map(toast => (
        <ToastNotification
          key={toast.id}
          notification={toast}
          onDismiss={() => toast.id && handleDismissToast(toast.id)}
        />
      ))}
    </div>
  );
};

const Notifications = () => {
  const { notificationDrawerOpen, setNotificationDrawerOpen } = useUI();
  const {
    notifications,
    dismissNotification,
    fetchNotifications,
    markNotificationRead,
    deleteNotification,
  } = useNotifications();
  const { status } = useSession();
  const { loggedIn } = useUser();
  const { push } = useRouterContext();
  const [dbNotifications, setDbNotifications] = useState<DBNotification[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  // Check both NextAuth session and legacy auth
  const isLoggedIn = status === 'authenticated' || loggedIn;

  // Fetch database notifications when drawer opens (only if logged in)
  useEffect(() => {
    if (notificationDrawerOpen && isLoggedIn) {
      const loadNotifications = async () => {
        try {
          setIsLoading(true);
          const result = await fetchNotifications({ limit: PAGE_SIZE.NOTIFICATIONS, isRead: false });
          setDbNotifications(result.notifications);
        } catch (error) {
          trackError(asError(error), undefined, { action: 'loadNotifications', component: 'Notifications' });
        } finally {
          setIsLoading(false);
        }
      };
      void loadNotifications();
    }
  }, [notificationDrawerOpen, isLoggedIn, fetchNotifications]);

  const handleMarkRead = async (notificationId: string) => {
    if (!isLoggedIn) return;
    // Optimistically remove notification from list immediately
    setDbNotifications(prev => prev.filter(n => n.id !== notificationId));
    // Mark as read on server in background
    void markNotificationRead(notificationId);
  };

  const handleDelete = async (notificationId: string) => {
    if (!isLoggedIn) return;
    // Optimistically remove notification from list immediately
    setDbNotifications(prev => prev.filter(n => n.id !== notificationId));
    // Delete on server in background
    void deleteNotification(notificationId);
  };

  // Combine temp notifications and database notifications
  // Each keeps its place in the full list: dismissNotification marks by that
  // index. Numbering the filtered list instead dismissed the wrong toast (or one
  // already dismissed) as soon as any toast before it had been dismissed.
  const tempNotifications = notifications
    .map((n, index) => ({ n, index }))
    .filter(({ n }) => !n.dismissed);
  const hasAnyNotifications = tempNotifications.length > 0 || dbNotifications.length > 0;

  return (
    <>
      <div
        className="notifications-bg"
        onClick={() => {
          setNotificationDrawerOpen(false);
        }}
      />
        <PanelDismiss
          className="notifications-cont"
          side="Right"
          expanded={notificationDrawerOpen}
          handle={null}
          onChange={() => {
            setNotificationDrawerOpen(false);
          }}
        >
          <div className="notifications  same-color">
            <div className="inner">
              <h3 className="notifications-header">{header}</h3>
              <div className="notification-scroller">
                {isLoading ? (
                  <div className="flex justify-center items-center py-20">
                    <div className="text-center">
                      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4" />
                      <p className="text-gray-600 dark:text-gray-400">Loading notifications...</p>
                    </div>
                  </div>
                ) : hasAnyNotifications ? (
                  <>
                    {/* Show database notifications first */}
                    {dbNotifications.map((n) => (
                      <DBNotificationItem
                        key={n.id}
                        notification={n}
                        push={push}
                        setNotificationDrawerOpen={setNotificationDrawerOpen}
                        markRead={handleMarkRead}
                        deleteNotif={handleDelete}
                      />
                    ))}

                    {/* Then show temporary toast notifications */}
                    {tempNotifications.map(({ n, index }) => (
                      <Notification
                        index={index}
                        dismiss={dismissNotification}
                        setNotificationDrawerOpen={setNotificationDrawerOpen}
                        msg={n.msg}
                        key={n.id || index}
                        classType={n.type}
                        push={push}
                        actionURL={n.actionURL}
                      />
                    ))}
                  </>
                ) : (
                  <CTABox
                    heading={noNotificationsCTAHeading}
                    explain={noNotificationsCTAExplain}
                    actionLabel={noNotificationsCTAButton}
                    actionFunction={() => {
                      googleEvent("no_notifications_cta");
                      setNotificationDrawerOpen(false);
                      push("/");
                    }}
                  />
                )}
              </div>
            </div>
          </div>
        </PanelDismiss>
    </>
  );
};

export default Notifications;
